/**
 * @file useBalance.js
 * @description 用户中心橘子币余额与充值档位（走 package/list，无差额/推荐档）
 * @module features/user/composables/useBalance
 */

import { computed, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/user'
import { fetchGoldPackageListApi } from '@/features/user/api/goldRechargeApi'
import { openGoldRecharge } from '@/features/user/composables/useGoldRecharge'
import { requestOpenAgreement } from '@/shared/utils/payDialogBridge'
import { getApiErrorMessageByCode } from '@/shared/utils/apiErrorCodes'

export function useBalance() {
  const userStore = useUserStore()
  const { user } = storeToRefs(userStore)

  const selectedPackageCode = ref('')
  const packages = ref(/** @type {import('../api/goldRechargeTypes').GoldPackageVo[]} */ ([]))
  const listLoading = ref(false)
  const errorMessage = ref('')
  const agreementTitle = ref('橘子币充值协议')

  const goldAmount = computed(() => user.value?.userGoldInfo?.goldAmount ?? 0)
  const totalConsumeAmount = computed(
    () => user.value?.userGoldInfo?.totalConsumeAmount ?? 0
  )
  const selectedPackage = computed(
    () => packages.value.find((item) => item.code === selectedPackageCode.value) || null
  )

  /**
   * @param {string} code
   */
  function selectPackage(code) {
    selectedPackageCode.value = code
    errorMessage.value = ''
  }

  async function loadPackages() {
    listLoading.value = true
    errorMessage.value = ''
    try {
      const data = await fetchGoldPackageListApi()
      const list = Array.isArray(data?.list) ? data.list : []
      packages.value = [...list].sort((a, b) => (b.sort || 0) - (a.sort || 0))
      agreementTitle.value = data?.agreementTitle || '橘子币充值协议'
      if (!selectedPackageCode.value && packages.value.length) {
        selectedPackageCode.value = packages.value[0].code
      }
    } catch (err) {
      const code = err && typeof err === 'object' && 'code' in err ? err.code : null
      errorMessage.value = getApiErrorMessageByCode(code) || (err instanceof Error ? err.message : '获取充值档位失败')
    } finally {
      listLoading.value = false
    }
  }

  function openRechargePay() {
    if (!selectedPackageCode.value) {
      errorMessage.value = '请选择充值档位'
      return
    }
    openGoldRecharge({
      source: 'user-center',
      packageCode: selectedPackageCode.value,
    })
  }

  function openGoldAgreement() {
    requestOpenAgreement('GOLD_RECHARGE')
  }

  onMounted(() => {
    void loadPackages()
  })

  return {
    selectedPackageCode,
    selectedPackage,
    packages,
    listLoading,
    errorMessage,
    agreementTitle,
    goldAmount,
    totalConsumeAmount,
    selectPackage,
    openRechargePay,
    openGoldAgreement,
    loadPackages,
  }
}
